import { ListItemIcon, ListItemText, Menu, MenuItem, useTheme } from '@mui/material';
import { PopoverOption } from '@types';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

interface NavbarPopoverProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  onToggle: (toggleAction: string) => void;
  options: PopoverOption[];
  activePage?: null | string;
  toggleStates?: Record<string, boolean>;
}

const NavbarPopover = ({ anchorEl, open, onClose, onToggle, options, activePage, toggleStates }: NavbarPopoverProps) => {
  const theme = useTheme();
  const navigate = useNavigate();

  const handleOptionClick = useCallback(
    (option: PopoverOption) => {
      onClose();
      if (option.toggleAction) {
        onToggle(option.toggleAction);
      } else if (option.path) {
        navigate(option.path);
      }
    },
    [navigate, onClose, onToggle]
  );

  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      autoFocus={false}
      disableAutoFocusItem
      disableEnforceFocus
      anchorOrigin={{
        vertical: 'center',
        horizontal: 'right'
      }}
      transformOrigin={{
        vertical: 'center',
        horizontal: 'left'
      }}
      slotProps={{
        paper: {
          sx: {
            minWidth: 140
          }
        }
      }}
    >
      {options.map((option) => {
        const isActive = option.toggleAction ? toggleStates?.[option.toggleAction] : activePage === option.path;
        return (
          <MenuItem
            key={option.path || option.toggleAction}
            onClick={() => handleOptionClick(option)}
            selected={false}
            sx={{
              '&.Mui-focusVisible': {
                backgroundColor: 'transparent'
              }
            }}
          >
            <ListItemIcon sx={{ color: isActive ? theme.palette.primary.main : theme.palette.action.active }}>
              {isActive ? (
                <option.ActiveIcon fontSize="small" />
              ) : option.InactiveIcon ? (
                <option.InactiveIcon fontSize="small" />
              ) : (
                <option.ActiveIcon fontSize="small" />
              )}
            </ListItemIcon>

            <ListItemText
              primary={option.label}
              slotProps={{
                primary: {
                  sx: {
                    fontSize: '12px',
                    fontStyle: 'normal',
                    lineHeight: 'normal',
                    fontWeight: isActive ? 700 : 400,
                    color: isActive ? theme.palette.primary.main : theme.palette.text.secondary
                  }
                }
              }}
            />
          </MenuItem>
        );
      })}
    </Menu>
  );
};

export default NavbarPopover;
